import { Select } from "./select";
import { cn } from "@hey404/ui";
import { useEffect, useState, type FormEvent, type ReactNode } from "react";
import { ConfirmDialog, useConfirm } from "./confirm-dialog";
import { motion } from "motion/react";
import {
  Bell,
  BookOpen,
  ChevronLeft,
  Copy,
  Download,
  MessageCircle,
  Plus,
  RefreshCw,
  Save,
  Trash2,
  Wrench,
} from "lucide-react";
import { apiFetch, apiUrl, AUTH_TOKEN_KEY } from "./lib/auth";
import { AdminShellSkeleton } from "./loading";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import {
  CreateModal,
  MarkdownField,
  btnPrimary,
  btnSecondary,
  inputClass,
  labelClass,
  btnAccent,
  btnDanger,
  adminErrorClass,
} from "./admin-ui";
import { PageHeader } from "./design-system";
import { AdminCourseCard } from "./admin-course-card";
import { AuditAdmin } from "./admin-audit";
import { CouponsAdmin } from "./admin-coupons";
import { TableScroll } from "./table-scroll";
import { Dialog } from "./dialog";
import {
  CollapseToggle,
  sidebarLabel,
  useSidebarCollapsed,
} from "./sidebar-collapse";
import { CurriculumTree } from "./curriculum/curriculum-tree";
import { useToast } from "./toast";
import type { Module } from "./curriculum/types";
import {
  adminSections,
  AdministratorsAdmin,
  CommerceAdmin,
  ModerationAdmin,
  OverviewAdmin,
  overviewQuery,
  PeopleAdmin,
  LearningToolsAdmin,
  adminSectionFromPath,
  adminSectionPaths,
  type AdminSection,
} from "./admin-operations";

type Forum = {
  enabled: boolean;
  title: string;
  description: string;
  requireApproval: boolean;
};
type Course = {
  id: string;
  slug: string;
  title: string;
  description: string;
  subtitle: string;
  category: string;
  level: "beginner" | "intermediate" | "advanced";
  language: string;
  requirements: string;
  outcomes: string;
  thumbnailUrl: string | null;
  instructorId: string | null;
  status: "draft" | "published" | "archived";
  access: "free" | "paid" | "subscription";
  saleStatus: "on_sale" | "coming_soon";
  basePriceMinor: number;
  baseCurrency: "USD" | "ARS" | "COP";
  includedMonthly: boolean;
  includedAnnual: boolean;
  prices: Array<{ currency: string; amountMinor: number }>;
  modules: Module[];
  forum: Forum | null;
};

const parseStringList = (value: string) => {
  try {
    const parsed = JSON.parse(value) as unknown;
    return Array.isArray(parsed)
      ? parsed.filter((item): item is string => typeof item === "string")
      : [];
  } catch {
    return [];
  }
};

// Helper text under a course-form field — normal-case + de-emphasized so the
// uppercase labels stay scannable while every field gets guidance (UNB2-164).
function Hint({ children }: { children: ReactNode }) {
  return (
    <span className="text-ink-soft mt-1 block text-xs font-normal normal-case">
      {children}
    </span>
  );
}

// One title per admin page: the PageHeader carries the section name and this
// line under it, so the sections themselves don't repeat their title.
const sectionDescriptions: Partial<Record<AdminSection, string>> = {
  overview: "El estado de la plataforma de un vistazo.",
  commerce: "Estado operativo de órdenes, suscripciones y comprobantes.",
  coupons:
    "Campañas de descuento: crea códigos, ponles reglas y mira quién los usó.",
  administrators: "Delega la operación con permisos explícitos.",
  people: "Busca usuarios y administra su acceso a cursos.",
  learning_tools: "Organiza escuelas, rutas y experiencias en vivo.",
  moderation:
    "Aprueba los posts del foro pendientes y modera los comentarios de clase.",
  audit: "Cada cambio sensible del equipo: quién lo hizo, sobre qué y cuándo.",
};

export function AdminPanel({ path }: { path: string }) {
  const [askConfirm, confirmDialog] = useConfirm();
  const queryClient = useQueryClient();
  // Same remembered preference (and animation) as the student sidebar.
  const [collapsed, setCollapsed] = useSidebarCollapsed();
  // Entrance animations run only in response to the user (expanding the
  // sidebar, switching section), never on the first paint, so the panel
  // appears in one piece instead of element by element.
  const [labelMotion, setLabelMotion] = useState(false);
  const [sectionMotion, setSectionMotion] = useState(false);
  const [courses, setCourses] = useState<Course[]>([]);
  const [courseOptions, setCourseOptions] = useState<
    Array<{ id: string; title: string }>
  >([]);
  const [selectedId, setSelectedId] = useState("");
  const [permissions, setPermissions] = useState<AdminSection[]>([]);
  const [canManageLifecycle, setCanManageLifecycle] = useState(false);
  const [error, setError] = useState("");
  const toast = useToast();
  const [courseModalOpen, setCourseModalOpen] = useState(false);
  const [section, setSection] = useState<AdminSection>(
    adminSectionFromPath(path) ?? "overview",
  );
  const [courseSearch, setCourseSearch] = useState("");
  const selected = courses.find((course) => course.id === selectedId);
  // Controlled copy of the course's access type, so the price fields can show
  // only when they apply (UNB2-164). Reset when switching courses.
  const [accessValue, setAccessValue] = useState<Course["access"]>("paid");
  useEffect(() => {
    const course = courses.find((c) => c.id === selectedId);
    if (course) setAccessValue(course.access);
  }, [selectedId, courses]);
  // Existing categories, offered as a combobox instead of free text (UNB2-164).
  const categoryOptions = Array.from(
    new Set(courses.map((c) => c.category).filter(Boolean)),
  ).sort();
  const refresh = async (keepId?: string) => {
    const result = await apiFetch<{
      courses: Course[];
      instructors: Array<{ id: string; name: string }>;
    }>("/api/admin/courses");
    setCourses(result.courses);
    setCourseOptions(result.courses);
    // Start in the grid ("" = no selection); a mutation with keepId stays on it.
    setSelectedId((prev) => keepId ?? prev);
  };
  const bootstrap = useQuery({
    queryKey: ["admin", "bootstrap"],
    queryFn: async () => {
      const current = await apiFetch<{
        permissions: AdminSection[];
        administrator: { role: string };
      }>("/api/admin/me");
      // Fetch the landing section's data alongside the course list, so the
      // shell skeleton holds until the whole first screen can paint at once.
      const requested = adminSectionFromPath(path);
      const landing =
        requested && current.permissions.includes(requested)
          ? requested
          : adminSections.find((item) => current.permissions.includes(item.id))
              ?.id;
      const [courseData] = await Promise.all([
        current.permissions.includes("courses")
          ? apiFetch<{
              courses: Course[];
              instructors: Array<{ id: string; name: string }>;
            }>("/api/admin/courses")
          : current.permissions.includes("people")
            ? apiFetch<{ courses: Array<{ id: string; title: string }> }>(
                "/api/admin/course-options",
              )
            : Promise.resolve({ courses: [] }),
        landing === "overview"
          ? queryClient.ensureQueryData(overviewQuery).catch(() => null)
          : null,
      ]);
      return { current, courseData };
    },
  });
  useEffect(() => {
    if (bootstrap.data) {
      const { current, courseData } = bootstrap.data;
      setPermissions(current.permissions);
      setCanManageLifecycle(
        ["owner", "administrator", "content_manager"].includes(
          current.administrator.role,
        ),
      );
      const firstSection = adminSections.find((item) =>
        current.permissions.includes(item.id),
      )?.id;
      const requested = adminSectionFromPath(path);
      const destination =
        requested && current.permissions.includes(requested)
          ? requested
          : (firstSection ?? "overview");
      setSection(destination);
      if (!requested || !current.permissions.includes(requested))
        window.history.replaceState({}, "", adminSectionPaths[destination]);
      if (current.permissions.includes("courses")) {
        const result = courseData as {
          courses: Course[];
          instructors: Array<{ id: string; name: string }>;
        };
        setCourses(result.courses);
        setCourseOptions(result.courses);
      } else if (current.permissions.includes("people")) {
        const result = courseData as {
          courses: Array<{ id: string; title: string }>;
        };
        setCourseOptions(result.courses);
      }
    }
    if (bootstrap.isError) setError("No pudimos cargar la administración.");
  }, [bootstrap.data, bootstrap.isError, path]);
  const mutate = async (
    action: () => Promise<unknown>,
    message: string,
    courseId?: string,
  ) => {
    try {
      await action();
      await queryClient.invalidateQueries({ queryKey: ["admin"] });
      await refresh(courseId);
      toast(message);
      return true;
    } catch (error) {
      // A publish blocked by the checklist returns the exact reasons — surface
      // them instead of the generic message so the admin knows what to fix.
      const body = (error as { body?: { error?: string; blockers?: string[] } })
        .body;
      if (body?.error === "not_publishable" && body.blockers?.length)
        toast(
          `No se puede publicar todavía. Falta: ${body.blockers.join(" · ")}`,
          "error",
        );
      else
        toast(
          "No pudimos guardar el cambio. Revisa los datos e intenta nuevamente.",
          "error",
        );
      return false;
    }
  };
  const formJson = (event: FormEvent<HTMLFormElement>) =>
    Object.fromEntries(new FormData(event.currentTarget));
  // The panel stays mounted across /admin/* routes, so browser back/forward
  // (a new `path`) must move the section too.
  useEffect(() => {
    const requested = adminSectionFromPath(path);
    if (requested && permissions.includes(requested)) setSection(requested);
  }, [path, permissions]);
  if (bootstrap.isPending) return <AdminShellSkeleton />;
  if (bootstrap.isError)
    return (
      <div role="alert" className={`${adminErrorClass} p-6`}>
        <h1 className="font-display text-2xl font-extrabold">
          No pudimos cargar la administración
        </h1>
        <p className="text-ink-soft mt-2 text-sm">
          Revisa tu conexión y permisos antes de volver a intentar.
        </p>
      </div>
    );
  const visibleSections = adminSections.filter((item) =>
    permissions.includes(item.id),
  );
  const go = (id: AdminSection) => {
    if (id === section) return;
    setSectionMotion(true);
    setSection(id);
    window.history.pushState({}, "", adminSectionPaths[id]);
    window.dispatchEvent(new PopStateEvent("popstate"));
    window.scrollTo({ top: 0 });
  };
  const activeLabel =
    visibleSections.find((item) => item.id === section)?.label ??
    "Administración";
  const filteredCourses = courses.filter((course) =>
    `${course.title} ${course.slug}`
      .toLowerCase()
      .includes(courseSearch.toLowerCase()),
  );
  const adminSidebar = (
    <aside
      className={`border-ink bg-ink text-paper md:border-r-(length:--border-surface) hidden shrink-0 flex-col gap-1 overflow-y-auto overflow-x-hidden py-4 transition-[width,padding] duration-300 ease-[cubic-bezier(0.22,0.8,0.25,1)] md:sticky md:top-16 md:flex md:h-[calc(100dvh-64px)] ${collapsed ? "px-2 md:w-[76px]" : "px-4 md:w-[236px]"}`}
    >
      <div
        className={`pb-5 pt-1 ${collapsed ? "flex justify-center" : "px-2"}`}
      >
        {collapsed ? (
          <span
            aria-hidden="true"
            title="Administración"
            className="border-ink bg-lemon border-chip shadow-brutal text-ink grid h-9 w-9 rotate-[-4deg] place-items-center"
          >
            <Wrench size={16} />
          </span>
        ) : (
          <span
            className={`border-ink bg-lemon border-chip shadow-brutal text-ink ${labelMotion ? "sidebar-fade-in" : ""} inline-block rotate-[-2deg] px-3 py-1 font-mono text-xs font-bold uppercase tracking-[0.08em]`}
          >
            Administración
          </span>
        )}
      </div>
      <nav className="flex flex-col gap-1">
        {visibleSections.map((item) => {
          const Icon = item.icon;
          const active = section === item.id;
          return (
            <button
              key={item.id}
              onClick={() => go(item.id)}
              aria-current={active ? "page" : undefined}
              // Same nav link as the student sidebar: regular weight, accent
              // fill and inset ink ring on hover and on the current section.
              title={collapsed ? item.label : undefined}
              className={`rounded-control flex min-h-11 shrink-0 items-center py-2.5 text-left text-sm transition-[background-color,color,gap,padding] duration-300 ${collapsed ? "justify-center gap-0 px-0" : "gap-3 px-3"} ${active ? "bg-acid text-ink shadow-nav" : "text-paper/90 hover:bg-bubble hover:text-ink hover:shadow-nav"}`}
            >
              <Icon size={17} aria-hidden="true" className="shrink-0" />
              <span className={sidebarLabel(collapsed)}>{item.label}</span>
            </button>
          );
        })}
      </nav>
      <div className="mt-auto w-full pt-6">
        <CollapseToggle
          collapsed={collapsed}
          onToggle={() => {
            setLabelMotion(true);
            setCollapsed((value) => !value);
          }}
        />
      </div>
    </aside>
  );
  const nonCoursesContent =
    section === "overview" ? (
      <OverviewAdmin />
    ) : section === "people" ? (
      <PeopleAdmin courses={courseOptions} />
    ) : section === "administrators" ? (
      <AdministratorsAdmin />
    ) : section === "moderation" ? (
      <ModerationAdmin />
    ) : section === "learning_tools" ? (
      <LearningToolsAdmin courses={courseOptions} />
    ) : section === "commerce" ? (
      <CommerceAdmin />
    ) : section === "coupons" ? (
      <CouponsAdmin />
    ) : (
      <AuditAdmin />
    );
  return (
    <div className="flex min-h-[calc(100dvh-64px)] flex-col md:flex-row">
      {confirmDialog}
      {adminSidebar}
      {/* Only the content fades between sections; the sidebar stays mounted
          and still. main.tsx keys its page transition on "/admin" for the
          whole panel, so the section change is animated here instead. */}
      <motion.div
        key={section}
        className="min-w-0 flex-1 p-5 sm:p-8"
        initial={sectionMotion ? { opacity: 0, y: 6 } : false}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.22, ease: "easeOut" }}
      >
        {error && (
          <p role="alert" className={`${adminErrorClass} mb-6`}>
            {error}
          </p>
        )}
        {section !== "courses" ? (
          <div>
            <PageHeader
              kicker="Administración"
              title={activeLabel}
              description={sectionDescriptions[section]}
              doodle="key-lavender"
              tone="lemon"
            />
            <div className="mt-9">{nonCoursesContent}</div>
          </div>
        ) : selected ? (
          <section className="space-y-6">
            <button
              type="button"
              onClick={() => setSelectedId("")}
              className="font-display flex items-center gap-1.5 text-sm font-bold hover:underline"
            >
              <ChevronLeft size={16} /> Volver a cursos
            </button>
            <form
              key={selected.id}
              onSubmit={(event) => {
                event.preventDefault();
                const data = formJson(event);
                mutate(
                  () =>
                    apiFetch(`/api/admin/courses/${selected.id}`, {
                      method: "PATCH",
                      body: JSON.stringify({
                        title: data.title,
                        slug: data.slug,
                        description: data.description,
                        subtitle: data.subtitle,
                        category: data.category,
                        level: data.level,
                        language: data.language,
                        requirements: JSON.stringify(
                          String(data.requirements)
                            .split("\n")
                            .map((item) => item.trim())
                            .filter(Boolean),
                        ),
                        outcomes: JSON.stringify(
                          String(data.outcomes)
                            .split("\n")
                            .map((item) => item.trim())
                            .filter(Boolean),
                        ),
                        thumbnailUrl: data.thumbnailUrl || null,
                        status: data.status,
                        access: data.access,
                        saleStatus: data.saleStatus,
                        baseCurrency: data.baseCurrency,
                        basePriceMinor: Math.round(
                          Number(data.basePrice) * 100,
                        ),
                        includedMonthly: data.includedMonthly === "on",
                        includedAnnual: data.includedAnnual === "on",
                        prices: {
                          USD: data.usdPrice
                            ? Math.round(Number(data.usdPrice) * 100)
                            : null,
                          ARS: data.arsPrice
                            ? Math.round(Number(data.arsPrice) * 100)
                            : null,
                          COP: data.copPrice
                            ? Math.round(Number(data.copPrice) * 100)
                            : null,
                        },
                      }),
                    }),
                  "Curso guardado.",
                  selected.id,
                );
              }}
              className="border-ink bg-bone shadow-brutal border-surface p-5"
            >
              <div className="flex items-center justify-between gap-3">
                <h2 className="font-display text-2xl font-extrabold">
                  Datos del curso
                </h2>
                <div className="flex flex-wrap gap-2">
                  {canManageLifecycle && (
                    <button
                      type="button"
                      onClick={() =>
                        mutate(
                          () =>
                            apiFetch(
                              `/api/admin/courses/${selected.id}/duplicate`,
                              { method: "POST" },
                            ),
                          "Curso duplicado como borrador.",
                        )
                      }
                      className={`${btnSecondary} min-h-10 gap-1.5 px-3 py-1.5 text-sm`}
                    >
                      <Copy size={14} aria-hidden="true" /> Duplicar
                    </button>
                  )}
                  {canManageLifecycle && (
                    <button
                      type="button"
                      onClick={async () => {
                        const sure = await askConfirm({
                          title: `¿Eliminar «${selected.title}»?`,
                          message:
                            "Se borran sus módulos, clases, videos, materiales y quizzes, y las personas inscritas pierden el acceso. Esta acción no se puede deshacer.",
                          confirmLabel: "Eliminar curso",
                          destructive: true,
                        });
                        if (sure)
                          mutate(
                            () =>
                              apiFetch(`/api/admin/courses/${selected.id}`, {
                                method: "DELETE",
                              }),
                            "Curso eliminado.",
                          );
                      }}
                      className={`${btnDanger} min-h-10 gap-1.5 px-3 py-1.5 text-sm`}
                    >
                      <Trash2 size={14} aria-hidden="true" /> Eliminar
                    </button>
                  )}
                </div>
              </div>
              <div className="mt-5 grid gap-4 sm:grid-cols-2">
                <label className={labelClass}>
                  Título
                  <input
                    name="title"
                    defaultValue={selected.title}
                    required
                    className={inputClass}
                  />
                </label>
                <label className={labelClass}>
                  Slug
                  <input
                    name="slug"
                    defaultValue={selected.slug}
                    required
                    className={inputClass}
                  />
                  <Hint>
                    URL del curso, sin espacios ni mayúsculas (ej.
                    tu-primera-app).
                  </Hint>
                </label>
                <MarkdownField
                  name="description"
                  label="Descripción (Markdown)"
                  defaultValue={selected.description}
                />
                <label className={`${labelClass} sm:col-span-2`}>
                  Subtítulo
                  <input
                    name="subtitle"
                    defaultValue={selected.subtitle}
                    className={inputClass}
                  />
                </label>
                <label className={labelClass}>
                  Categoría
                  <input
                    name="category"
                    defaultValue={selected.category}
                    list="course-categories"
                    className={inputClass}
                  />
                  <datalist id="course-categories">
                    {categoryOptions.map((option) => (
                      <option key={option} value={option} />
                    ))}
                  </datalist>
                  <Hint>
                    Elige una existente o escribe una nueva. (Pronto se van a
                    poder gestionar desde Academia.)
                  </Hint>
                </label>
                <label className={labelClass}>
                  Nivel
                  <Select
                    name="level"
                    defaultValue={selected.level}
                    className={inputClass}
                  >
                    <option value="beginner">Inicial</option>
                    <option value="intermediate">Intermedio</option>
                    <option value="advanced">Avanzado</option>
                  </Select>
                </label>
                <label className={labelClass}>
                  Idioma
                  <input
                    name="language"
                    defaultValue={selected.language}
                    className={inputClass}
                  />
                </label>
                <label className={labelClass}>
                  Portada (URL)
                  <input
                    name="thumbnailUrl"
                    type="url"
                    defaultValue={selected.thumbnailUrl ?? ""}
                    className={inputClass}
                  />
                </label>
                <label className={labelClass}>
                  Requisitos (uno por línea)
                  <textarea
                    name="requirements"
                    rows={5}
                    defaultValue={parseStringList(selected.requirements).join(
                      "\n",
                    )}
                    className={inputClass}
                  />
                </label>
                <label className={labelClass}>
                  Qué vas a aprender (uno por línea)
                  <textarea
                    name="outcomes"
                    rows={5}
                    defaultValue={parseStringList(selected.outcomes).join("\n")}
                    className={inputClass}
                  />
                </label>
                <label className={labelClass}>
                  Estado
                  <Select
                    name="status"
                    defaultValue={selected.status}
                    className={inputClass}
                  >
                    <option value="draft">Borrador</option>
                    <option value="published">Publicado</option>
                    <option value="archived">Archivado</option>
                  </Select>
                  <Hint>
                    Borrador: no visible para alumnos. Publicado: visible en el
                    catálogo. Archivado: fuera del catálogo.
                  </Hint>
                </label>
                <label className={labelClass}>
                  Tipo de acceso
                  <Select
                    name="access"
                    value={accessValue}
                    onChange={(event) =>
                      setAccessValue(event.target.value as Course["access"])
                    }
                    className={inputClass}
                  >
                    <option value="free">Gratis</option>
                    <option value="paid">Pago individual</option>
                    <option value="subscription">Solo suscripción</option>
                  </Select>
                  <Hint>
                    Gratis: sin costo. Pago individual: se compra suelto (define
                    el precio abajo). Solo suscripción: se accede únicamente con
                    un plan.
                  </Hint>
                </label>
                <label className={labelClass}>
                  Venta
                  <Select
                    name="saleStatus"
                    defaultValue={selected.saleStatus}
                    className={inputClass}
                  >
                    <option value="on_sale">En venta</option>
                    <option value="coming_soon">
                      Próximamente (lista de espera)
                    </option>
                  </Select>
                  <Hint>
                    En venta: se puede comprar. Próximamente: junta lista de
                    espera en vez de vender.
                  </Hint>
                </label>
                {/* Price fields apply only to individually-paid courses. Kept
                    mounted (display:none) when hidden so they still submit. */}
                <div className={accessValue === "paid" ? "contents" : "hidden"}>
                  <label className={labelClass}>
                    Moneda base
                    <Select
                      name="baseCurrency"
                      defaultValue={selected.baseCurrency}
                      className={inputClass}
                    >
                      <option value="USD">USD</option>
                      <option value="ARS">ARS</option>
                      <option value="COP">COP</option>
                    </Select>
                  </label>
                  <label className={labelClass}>
                    Precio base
                    <input
                      name="basePrice"
                      type="number"
                      min="0"
                      step="0.01"
                      defaultValue={selected.basePriceMinor / 100}
                      className={inputClass}
                    />
                    <Hint>
                      En la moneda base. Los precios fijos por moneda de abajo
                      lo pisan si los cargas.
                    </Hint>
                  </label>
                  {(["USD", "ARS", "COP"] as const).map((currency) => (
                    <label key={currency} className={labelClass}>
                      Precio fijo {currency} (opcional)
                      <input
                        name={`${currency.toLowerCase()}Price`}
                        type="number"
                        min="0"
                        step="0.01"
                        defaultValue={(() => {
                          const price = selected.prices.find(
                            (item) => item.currency === currency,
                          );
                          return price ? price.amountMinor / 100 : "";
                        })()}
                        className={inputClass}
                      />
                    </label>
                  ))}
                </div>
                {/* Subscription inclusion is irrelevant for free courses. */}
                <div className={accessValue === "free" ? "hidden" : "contents"}>
                  <label className="flex items-center gap-2 font-mono text-xs font-bold uppercase">
                    <input
                      name="includedMonthly"
                      type="checkbox"
                      defaultChecked={selected.includedMonthly}
                    />{" "}
                    Incluido en mensual
                  </label>
                  <label className="flex items-center gap-2 font-mono text-xs font-bold uppercase">
                    <input
                      name="includedAnnual"
                      type="checkbox"
                      defaultChecked={selected.includedAnnual}
                    />{" "}
                    Incluido en anual
                  </label>
                </div>
              </div>
              <button className="border-ink bg-ink font-display text-paper border-chip mt-5 flex items-center gap-2 px-4 py-2 text-sm font-bold">
                <Save size={15} /> Guardar curso
              </button>
            </form>
            <WaitlistPanel
              courseId={selected.id}
              saleStatus={selected.saleStatus}
            />
            <CurriculumTree
              courseId={selected.id}
              courseTitle={selected.title}
              modules={selected.modules}
              mutate={mutate}
            />
            <ForumEditor
              courseId={selected.id}
              forum={selected.forum}
              mutate={mutate}
            />
          </section>
        ) : (
          <div>
            <PageHeader
              kicker="Administración"
              title="Cursos"
              description="Elige un curso para editar su contenido, acceso y precios, o crea uno nuevo."
              doodle="key-lavender"
              tone="lemon"
            />
            <div className="mt-9 flex flex-wrap items-center gap-3">
              <div className="flex items-center gap-3">
                <h2 className="font-display text-2xl font-extrabold">
                  Todos los cursos
                </h2>
                <span className="border-ink bg-lemon border-chip rotate-[-2deg] px-2 py-0.5 font-mono text-xs font-bold">
                  {courses.length}
                </span>
              </div>
              <div className="ml-auto flex w-full flex-wrap gap-3 sm:w-auto">
                <input
                  value={courseSearch}
                  onChange={(event) => setCourseSearch(event.target.value)}
                  aria-label="Buscar curso por nombre"
                  placeholder="Buscar curso…"
                  className={cn(
                    inputClass,
                    "mt-0 min-w-0 flex-1 px-4 py-3 sm:w-72 sm:flex-none",
                  )}
                />
                {canManageLifecycle && (
                  <button
                    onClick={() => setCourseModalOpen(true)}
                    className={`${btnPrimary} shrink-0 gap-2 px-4 py-3 text-sm`}
                  >
                    <Plus size={16} /> Nuevo curso
                  </button>
                )}
              </div>
            </div>
            {filteredCourses.length ? (
              <div className="mt-6 grid gap-6 sm:grid-cols-2 xl:grid-cols-3">
                {filteredCourses.map((course) => {
                  // "USD 49": the code first reads clearer than "49 US$".
                  const price = course.basePriceMinor
                    ? `${course.baseCurrency} ${new Intl.NumberFormat("es", {
                        maximumFractionDigits: 0,
                      }).format(course.basePriceMinor / 100)}`
                    : null;
                  return (
                    <AdminCourseCard
                      key={course.id}
                      index={courses.indexOf(course)}
                      title={course.title}
                      subtitle={course.subtitle || course.description}
                      category={course.category}
                      thumbnailUrl={course.thumbnailUrl}
                      status={course.status}
                      saleStatus={course.saleStatus}
                      access={course.access}
                      price={price}
                      modules={course.modules.length}
                      lessons={course.modules.reduce(
                        (total, module) => total + module.lessons.length,
                        0,
                      )}
                      onOpen={() => setSelectedId(course.id)}
                    />
                  );
                })}
              </div>
            ) : (
              <div className="border-ink/30 bg-bone/60 border-chip mt-6 flex flex-col items-center border-dashed p-10 text-center">
                <BookOpen size={36} aria-hidden="true" />
                <p className="font-display mt-3 text-xl font-extrabold">
                  {courses.length
                    ? "Ningún curso coincide con la búsqueda."
                    : "Todavía no hay cursos."}
                </p>
                <p className="text-ink-soft mt-1 text-sm">
                  {courses.length
                    ? "Prueba con otra palabra del nombre."
                    : "Crea el primero con «Nuevo curso»."}
                </p>
              </div>
            )}
          </div>
        )}
      </motion.div>
      <CreateModal
        open={courseModalOpen}
        onClose={() => setCourseModalOpen(false)}
        title="Nuevo curso"
        description="Crea la estructura inicial. Después vas a poder configurar contenido, acceso y precios."
      >
        <form
          onSubmit={async (event) => {
            event.preventDefault();
            const data = formJson(event);
            const saved = await mutate(
              () =>
                apiFetch("/api/admin/courses", {
                  method: "POST",
                  body: JSON.stringify({
                    title: data.title,
                    slug: data.slug,
                    description: "",
                    status: "draft",
                    access: "paid",
                    basePriceMinor: 0,
                    baseCurrency: "USD",
                    // New courses join the membership by default (opt-out per
                    // course via the toggles below).
                    includedMonthly: true,
                    includedAnnual: true,
                  }),
                }),
              "Curso creado.",
            );
            if (saved) setCourseModalOpen(false);
          }}
          className="space-y-4"
        >
          <label className={labelClass}>
            Nombre del curso
            <input autoFocus name="title" required className={inputClass} />
          </label>
          <label className={labelClass}>
            Slug
            <input
              name="slug"
              required
              pattern="[a-z0-9]+(?:-[a-z0-9]+)*"
              placeholder="nombre-del-curso"
              className={inputClass}
            />
          </label>
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={() => setCourseModalOpen(false)}
              className={`${btnSecondary} px-4 py-2 text-sm`}
            >
              Cancelar
            </button>
            <button className={`${btnAccent} px-4 py-2 text-sm`}>
              Crear curso
            </button>
          </div>
        </form>
      </CreateModal>
    </div>
  );
}

function ForumEditor({
  courseId,
  forum,
  mutate,
}: {
  courseId: string;
  forum: Forum | null;
  mutate: (
    action: () => Promise<unknown>,
    message: string,
    courseId?: string,
  ) => Promise<boolean>;
}) {
  return (
    <form
      onSubmit={(event) => {
        event.preventDefault();
        const data = new FormData(event.currentTarget);
        mutate(
          () =>
            apiFetch(`/api/admin/courses/${courseId}/forum`, {
              method: "PUT",
              body: JSON.stringify({
                title: data.get("title"),
                description: data.get("description"),
                enabled: data.get("enabled") === "on",
                requireApproval: data.get("requireApproval") === "on",
              }),
            }),
          "Foro guardado.",
          courseId,
        );
      }}
      className="border-ink bg-lemon shadow-brutal border-surface p-5"
    >
      <div className="flex items-center gap-3">
        <MessageCircle />
        <h2 className="font-display text-2xl font-extrabold">Foro del curso</h2>
      </div>
      <div className="mt-5 grid gap-4 sm:grid-cols-2">
        <label className={labelClass}>
          Nombre
          <input
            name="title"
            defaultValue={forum?.title ?? "Foro del curso"}
            required
            className={inputClass}
          />
        </label>
        <MarkdownField
          name="description"
          label="Descripción del foro (Markdown)"
          defaultValue={forum?.description ?? ""}
          rows={3}
        />
        <label className="flex items-center gap-2 font-mono text-xs font-bold uppercase">
          <input
            name="enabled"
            type="checkbox"
            defaultChecked={forum?.enabled ?? true}
          />{" "}
          Foro habilitado
        </label>
        <label className="flex items-center gap-2 font-mono text-xs font-bold uppercase">
          <input
            name="requireApproval"
            type="checkbox"
            defaultChecked={forum?.requireApproval ?? false}
          />{" "}
          Moderación previa
        </label>
      </div>
      <button className="border-ink bg-ink font-display text-paper border-chip mt-5 flex items-center gap-2 px-4 py-2 text-sm font-bold">
        <Save size={15} /> Guardar foro
      </button>
    </form>
  );
}

type WaitlistRow = { email: string; name: string | null; createdAt: string };

/**
 * "Interesados" — the people who asked to be notified when a coming_soon course
 * launches. Shows the list, exports it as CSV, and fires the manual launch
 * email (server-side, idempotent). No automatic trigger on its own.
 */
function WaitlistPanel({
  courseId,
  saleStatus,
}: {
  courseId: string;
  saleStatus: "on_sale" | "coming_soon";
}) {
  const [data, setData] = useState<{
    count: number;
    waitlist: WaitlistRow[];
  } | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [result, setResult] = useState<{ ok: boolean; text: string } | null>(
    null,
  );
  // Optional launch details that dress up the sales ("apertura") email — the
  // course's live price is the exclusive one; these add the struck-through
  // normal price, the spots and the deadline the brief calls for.
  const [sales, setSales] = useState({
    regularPriceLabel: "",
    spotsLabel: "",
    deadlineLabel: "",
  });

  const load = () => {
    setError(false);
    return apiFetch<{ count: number; waitlist: WaitlistRow[] }>(
      `/api/admin/courses/${courseId}/waitlist`,
    )
      .then(setData)
      .catch(() => setError(true));
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load closes over courseId; reload only when it changes
  }, [courseId]);

  const exportCsv = async () => {
    const token = localStorage.getItem(AUTH_TOKEN_KEY);
    const res = await fetch(
      `${apiUrl}/api/admin/courses/${courseId}/waitlist?format=csv`,
      {
        credentials: "include",
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      },
    );
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = `interesados-${courseId}.csv`;
    anchor.click();
    URL.revokeObjectURL(url);
  };

  const doNotify = async () => {
    setBusy(true);
    try {
      const body = Object.fromEntries(
        Object.entries(sales).filter(([, value]) => value.trim()),
      );
      const res = await apiFetch<{ sent: number; skipped: number }>(
        `/api/admin/courses/${courseId}/waitlist/notify`,
        { method: "POST", body: JSON.stringify(body) },
      );
      setResult({
        ok: true,
        text:
          `Aviso enviado a ${res.sent} persona(s)` +
          (res.skipped ? ` · ${res.skipped} fallaron, reintenta` : "") +
          ".",
      });
      await load();
    } catch {
      setResult({ ok: false, text: "No pudimos enviar el aviso. Reintenta." });
    } finally {
      setBusy(false);
    }
  };

  const empty = !data || data.waitlist.length === 0;
  return (
    <section className="border-ink bg-bone shadow-brutal border-surface mt-6 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <h2 className="font-display text-2xl font-extrabold">Interesados</h2>
          <span className="border-ink bg-acid border-chip px-2 py-0.5 font-mono text-xs font-bold">
            {data?.count ?? 0}
          </span>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => void load()}
            className={`${btnSecondary} px-3 py-2 text-xs`}
          >
            <RefreshCw size={14} /> Refrescar
          </button>
          <button
            type="button"
            onClick={exportCsv}
            disabled={empty}
            className={`${btnSecondary} px-3 py-2 text-xs`}
          >
            <Download size={14} /> Exportar CSV
          </button>
          <button
            type="button"
            onClick={() => setConfirmOpen(true)}
            disabled={busy || empty}
            className={`${btnPrimary} px-3 py-2 text-xs`}
          >
            <Bell size={14} /> {busy ? "Enviando…" : "Avisar a la lista"}
          </button>
        </div>
      </div>
      <p className="text-ink-soft mt-1 text-sm">
        Personas que dejaron su email para que les avises cuando el curso esté
        disponible.
        {saleStatus === "on_sale" &&
          " El curso ya está en venta; volver a avisar solo alcanza a quienes se anotaron después del último envío."}
      </p>
      <details className="border-ink border-chip mt-4 p-3">
        <summary className="cursor-pointer font-mono text-xs font-bold uppercase">
          Datos del email de apertura (opcional)
        </summary>
        <p className="text-ink-soft mt-2 text-xs">
          El precio exclusivo sale del precio del curso. Estos campos agregan el
          precio normal tachado, los cupos y la fecha de corte.
        </p>
        <div className="mt-3 grid gap-2 sm:grid-cols-3">
          {(
            [
              ["regularPriceLabel", "Precio normal (ej: USD 99)"],
              ["spotsLabel", "Cupos (ej: 50 lugares)"],
              ["deadlineLabel", "Cierra (ej: el 7 de septiembre)"],
            ] as const
          ).map(([key, placeholder]) => (
            <input
              key={key}
              value={sales[key]}
              onChange={(event) =>
                setSales((prev) => ({ ...prev, [key]: event.target.value }))
              }
              placeholder={placeholder}
              className={cn(
                inputClass,
                "mt-0 w-full px-2 py-1.5 font-mono text-xs",
              )}
            />
          ))}
        </div>
      </details>
      {error ? (
        <p role="alert" className={`${adminErrorClass} mt-4 font-bold`}>
          No pudimos cargar los interesados. Toca “Refrescar” para reintentar.
        </p>
      ) : empty ? (
        <p className="text-ink-soft mt-4 text-sm">
          Todavía no hay interesados.
        </p>
      ) : (
        <TableScroll
          count={data!.waitlist.length}
          noun="interesados"
          className="mt-4"
        >
          <table className="data-table">
            <thead>
              <tr>
                <th>Email</th>
                <th>Nombre</th>
                <th>Fecha</th>
              </tr>
            </thead>
            <tbody>
              {data!.waitlist.map((row) => (
                <tr key={row.email}>
                  <td className="code">{row.email}</td>
                  <td>{row.name ?? "—"}</td>
                  <td className="text-ink-soft text-sm">
                    {new Date(row.createdAt).toLocaleDateString("es-AR")}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroll>
      )}

      <ConfirmDialog
        request={
          confirmOpen
            ? {
                title: "Avisar a la lista",
                message:
                  "Se enviará un email a las personas que todavía no fueron avisadas. Se puede repetir sin duplicar avisos.",
                confirmLabel: "Enviar avisos",
                onConfirm: doNotify,
              }
            : null
        }
        onClose={() => setConfirmOpen(false)}
      />
      <Dialog
        open={Boolean(result)}
        onClose={() => setResult(null)}
        title={result?.ok ? "Aviso enviado" : "No se pudo enviar"}
        description={result?.text}
        icon={Bell}
        tone={result?.ok ? "acid" : "lemon"}
        footer={
          <button
            type="button"
            onClick={() => setResult(null)}
            className={`${btnPrimary} px-4 py-2.5 text-sm`}
          >
            Entendido
          </button>
        }
      />
    </section>
  );
}
